import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { mapCompanyWorkspaceData } from '../lib/companyProfile';

/** Set by `Dashboard` only; absent elsewhere (e.g. Super Admin, auth pages). */
export const WorkspaceContext = createContext(null);

/**
 * Loads `companies/{companyId}` and exposes branding, modules, and AI context.
 * @param {{ companyId?: string | null, children: React.ReactNode }} props
 */
export function WorkspaceProvider({ companyId = null, children }) {
  const [workspace, setWorkspace] = useState(null);
  const [loading, setLoading] = useState(Boolean(companyId));

  const refresh = useCallback(async () => {
    if (!companyId) {
      setWorkspace(null);
      setLoading(false);
      return null;
    }
    setLoading(true);
    try {
      const snap = await getDoc(doc(db, 'companies', companyId));
      const mapped = snap.exists() ? mapCompanyWorkspaceData(snap.data(), snap.id) : null;
      setWorkspace(mapped);
      return mapped;
    } catch (e) {
      console.error('WorkspaceProvider:', e);
      setWorkspace(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    if (!companyId) {
      setWorkspace(null);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    const unsubscribe = onSnapshot(
      doc(db, 'companies', companyId),
      (snap) => {
        const mapped = snap.exists() ?
          mapCompanyWorkspaceData(snap.data(), snap.id) :
          null;
        setWorkspace(mapped);
        setLoading(false);
      },
      (e) => {
        console.error('WorkspaceProvider listener:', e);
        setWorkspace(null);
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, [companyId]);

  const value = useMemo(
    () => ({
      workspace,
      loading,
      refresh,
      companyId: companyId || null,
      /** Business App Hub flags from `companies/{id}.activeModules` (live). */
      activeModules: workspace?.activeModules ?? null,
      /** Granular RBAC map from `companies/{id}.rolePermissions` (live). */
      rolePermissions: workspace?.rolePermissions ?? null,
    }),
    [workspace, loading, refresh, companyId],
  );

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

const EMPTY_WORKSPACE_CTX = {
  workspace: null,
  loading: false,
  refresh: async () => null,
  companyId: null,
  activeModules: null,
  rolePermissions: null,
};

export function useWorkspace() {
  return useContext(WorkspaceContext) ?? EMPTY_WORKSPACE_CTX;
}

/** Branding + portal fields from the active workspace (legacy hook name). */
export function useWorkspaceBranding() {
  return useWorkspace().workspace;
}
